<template>
  <li
    class="draggable"
    :data-modifier-id="modifier.id"
  >
    <div class="row sub-row">
      <div class="is-flex pad-y-sm">
        <b-icon
          v-show="canSortResource && modifierGroup.type !== modifierGroupTypes.PORTION"
          icon="grip-lines"
          size="is-small"
          pack="far"
          class="drag-handle"
        />
        <img
          v-show="modifierGroup.type === modifierGroupTypes.PORTION"
          :class="['mar-r-sm', { 'transform-180': ['Right Half', '2nd Half'].includes(modifier.displayName) }]"
          :src="portionIcons[modifier.modifierTemplate]"
        >
        {{ modifier.displayName }}
      </div>
      <span class="align-center justify-end">
        <template v-if="!activeMenuType">
          <b-radio
            v-if="modifierGroup.maxAllowed === 1"
            :native-value="true"
            :disabled="!canSetAsDefault"
            :value="modifier.isDefault"
            :name="`${modifierGroup.id}-isDefault`"
            @input="handleSelectedChange({ isDefault: $event, isRadio: true })"
            @click.native="handleSelfClick($event, modifier.id)"
          />
          <b-checkbox
            v-else
            :disabled="!canSetAsDefault || !modifier.isDefault && hasGroupReachedDefaultModifierLimit"
            :value="modifier.isDefault"
            @input="handleSelectedChange({ isDefault: $event })"
          />
        </template>
      </span>
      <span class="align-center justify-end">
        <template v-if="!activeMenuType && modifierGroup.type !== modifierGroupTypes.PORTION">
          <b-button
            v-if="modifier.availabilityBeginDate || modifier.availabilityEndDate"
            v-tippy="{ content: 'Restricted Availability', placement: 'left', delay: [333, 0] }"
            class="is-transparent"
            @click="openModifierModal()"
          >
            <b-icon icon="calendar" pack="far" />
          </b-button>
          <b-button
            class="is-transparent"
            @click="openModifierModal()"
          >
            <b-icon
              v-if="$can('update', 'MenuItemModifier') && menuPermissions.EDIT_RESOURCE"
              icon="pencil"
              size="is-small"
            />
            <span v-else>View</span>
          </b-button>
          <b-dropdown
            v-if="$can('destroy', 'MenuItemModifier') && menuPermissions.REMOVE_RESOURCE"
            aria-role="list"
            position="is-bottom-left"
          >
            <b-button
              slot="trigger"
              class="is-transparent"
              type="is-white"
            >
              <b-icon icon="ellipsis-v" pack="far" size="is-small" />
            </b-button>
            <b-dropdown-item
              class="is-danger"
              @click="openDeleteModifierConfirmation(modifier)"
            >
              <b-icon icon="trash-alt" class="mar-r-sm" size="is-small" />Delete Modifier
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </span>
    </div>
  </li>
</template>

<script>
  import { mapState } from 'vuex';

  import { modifierGroupTypes, modifierGroupPortionTemplates } from '@/constants/modifierGroups';
  import posTypes from '@/constants/posTypes';

  import AlertModal from '@/components/globals/alert-modal.vue';

  import Modifier from '@/store/classes/Modifier';
  import ModifierGroup from '@/store/classes/ModifierGroup';

  import AddEditModifierModal from '../main-menu/add-edit-modifier-modal/index.vue';


  export default {
    name: 'NestedModifierRow',

    props: {
      modifier: {
        type: Object,
        required: true
      },

      modifierGroup: {
        type: Object,
        required: true
      },

      hasGroupReachedDefaultModifierLimit: {
        type: Boolean,
        required: true
      }
    },

    data: () => ({
      modifierGroupTypes,
      portionIcons: {
        [modifierGroupPortionTemplates.FULL_GROUP_TEMPLATE.constant]: '/images/whole-portion.svg',
        [modifierGroupPortionTemplates.HALF_GROUP_TEMPLATE.constant]: '/images/half-portion.svg'
      },
      posTypes
    }),

    computed: {
      ...mapState('mainMenu', [
        'menuPermissions',
        'featurePermissions',
        'selectedMerchant',
        'activeMenuType',
        'modifiersOpenedStatus'
      ]),

      canSortResource() {
        return [
          this.$can('update', 'MenuItemModifier'),
          !this.activeMenuType,
          this.menuPermissions.SORT_RESOURCE
        ].every(x => x);
      },

      canSetAsDefault() {
        return (
          this.$can('update', 'MenuItemModifier')
          && this.menuPermissions.SET_DEFAULT_MODIFIER
        );
      },

      sortingModifierId() {
        // It doesn't look like this was implemented at this level in the original main menu code
        // That seems a little fishy given the rest of the code, so investigate this further
        return ModifierGroup.$state().sortingParentModifierId;
      }
    },

    methods: {
      hasPosType(posTypeId) {
        // From merchantMixin, which we're not using in the main menu for performance reasons
        return this.selectedMerchant?.posConfigurations
          .some(posConfig => posConfig.posType.id === posTypeId);
      },

      async handleSelectedChange({ isDefault, isRadio }) { // eslint-disable-line object-curly-newline
        try {
          if (isRadio) {
            const updatedModifiers = JSON.parse(JSON.stringify(this.modifierGroup.modifiers)).map((modifier) => {
              modifier.isDefault = modifier.id === this.modifier.id;
              return modifier;
            });

            await Modifier.bulkUpdateModifiers({
              modifierGroup: this.modifierGroup,
              modifiers: this.modifierGroup.modifiers,
              updatedModifiers
            });
          }

          else {
            await Modifier.updateModifier({ id: this.modifier.id, isDefault });
          }

          this.$_onRequestSuccess({
            toastOptions: {
              message: 'Successfully updated modifier selection!'
            }
          });
        }

        catch (error) {
          this.$_onRequestError({
            error,
            toastOptions: {
              message: 'Unable to update modifier selection'
            }
          });
        }
      },

      handleSelfClick(event) {
        const isChecked = event.target.closest('.b-radio').querySelector('input').checked;

        if (isChecked && this.$can('update', 'MenuItemModifier')) {
          this.handleSelectedChange({ isDefault: !isChecked });
        }
      },

      openModifierModal() {
        this.$buefy.modal.open({
          parent: this,
          component: AddEditModifierModal,
          hasModalCard: true,
          trapFocus: true,
          canCancel: false,
          customClass: 'auto-width',
          events: {
            'modifier-updated': this.emitModifierUpdate
          },
          props: {
            modifierGroup: this.modifierGroup,
            mode: 'update',
            modifierId: this.modifier.id
          }
        });
      },

      emitModifierUpdate() {
        // not sure we need this anymore -- It was used for search, so will be handled in 4312
        console.log('emitting modifier update');
      },

      openDeleteModifierConfirmation({ displayName, id }) {
        this.$buefy.modal.open({
          parent: this,
          component: AlertModal,
          hasModalCard: true,
          trapFocus: true,
          customClass: 'auto-width',
          props: {
            buttons: [
              { text: 'Cancel' },
              {
                text: 'Delete',
                primary: true,
                onClick: () => this.deleteModifier({ displayName, id })
              }
            ],
            horizontal: true,
            showCloseButton: false,
            icon: 'trash-alt',
            title: 'Delete Modifier',
            message: `<b>${displayName}</b> will be deleted from all locations. Are you sure?`,
            type: 'is-danger'
          }
        });
      },

      async deleteModifier({ displayName, id }) {
        try {
          await Modifier.deleteModifier(id);

          this.$_onRequestSuccess({
            toastOptions: {
              message: `Successfully deleted <b>${displayName}</b>`
            }
          });
        }

        catch (error) {
          this.$_onRequestError({
            error,
            toastOptions: {
              message: `Unable to delete <b>${displayName}</b>`
            }
          });
        }
      },
    }
  };
</script>

<style lang='sass' scoped>
.row
    grid-template-columns: 1fr 200px 170px
</style>
